import { FormlyFieldConfig } from '@ngx-formly/core';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';

export class ContactFormFields {
  private isModelBusiness: boolean;
  private isAnOwner: boolean;

  constructor(
    private canSeeCountrySelect: boolean = false,
    private localeSettingService: LocaleSettingService
  ) {}

  contactFields(isBusiness: boolean = false, isAnOwner: boolean = false): FormlyFieldConfig[] {
    this.isModelBusiness = isBusiness;
    this.isAnOwner = isAnOwner;
    return [
      {
        fieldGroupClassName: 'formly-column',
        fieldGroup: [
          {
            className: 'formly-column-left',
            fieldGroup: [
              { // BEGIN First Name, Last Name, Title, Mobile Phone, Work Phone, & Email
                fieldGroupClassName: 'formly-field-group',
                fieldGroup: [
                  {
                    key: 'first',
                    type: 'text',
                    templateOptions: {
                      label: 'First Name',
                      attributes: {
                        'data-cy': 'first-name-input'
                      },
                        required: true,
                    },
                    validation: {
                      show: true,
                    },
                    expressionProperties: {
                      'templateOptions.label': () => isBusiness ? 'Business Name' : 'First Name'
                    }
                  },
                  {
                    key: 'last',
                    type: 'text',
                    templateOptions: {
                      label: 'Last Name',
                      attributes: {
                        'data-cy': 'last-name-input'
                      },
                      required: this.isAnOwner ? true : false
                    },
                    validation: {
                      show: true,
                    },
                    expressionProperties: {
                      'templateOptions.label': () => isBusiness ? 'Business Type' : 'Last Name',
                      'templateOptions.placeholder': () => isBusiness ? 'LLC, sCorp, cCorp, etc' : null
                    }
                  },
                  {
                    key: 'title',
                    type: 'text',
                    templateOptions: {
                      label: 'Title/Role',
                      attributes: {
                        'data-cy': 'title-input'
                      }
                    }
                  },
                  { // BEGIN Mobile Phone, Work Phone, & Email
                    fieldGroupClassName: 'formly-field-group',
                    fieldGroup: [
                      {
                        fieldGroup: [
                          {
                            key: 'mobilePhoneModel',
                            type: 'intl-phone',
                            templateOptions: {
                              label: 'Mobile Phone',
                              attributes: {
                                'data-cy': 'mobile-phone-input'
                              }
                            }
                          }
                        ]
                      },
                      {
                        fieldGroup: [
                          {
                            key: 'workPhoneModel',
                            type: 'intl-phone',
                            templateOptions: {
                              label: 'Work Phone',
                              attributes: {
                                'data-cy': 'work-phone-input'
                              }
                            }
                          }
                        ]
                      },
                      {
                        key: 'email',
                        type: 'text',
                        templateOptions: {
                          label: 'Email',
                          attributes: {
                            'data-cy': 'email-input'
                          },
                          pattern: /^\S+@\S+\.\S+$/i,
                          required: this.isAnOwner ? true : false
                        },
                        validation: {
                          show: true,
                        }
                      }
                    ]
                  }
                ]
              }, // END First Name, Last Name, Title, Mobile Phone, Work Phone, & Email
              { // BEGIN Date of Birth, Ownership, & Social Security #
                fieldGroupClassName: 'formly-field-group',
                hideExpression: !this.isAnOwner,
                fieldGroup: [
                  {
                    fieldGroupClassName: 'formly-sub-row',
                    fieldGroup: [
                      {
                        className: 'formly-sub-column formly-sub-column-padding',
                        fieldGroup: [
                          {
                            key: 'birthDate',
                            type: 'datepicker',
                            templateOptions: {
                              label: 'Date of Birth',
                              attributes: {
                                'data-cy': 'dob-input'
                              },
                              datepickerOptions: {
                                max: new Date()
                              },
                            },
                            validation: {
                              messages: {
                                matDatepickerMax: () => 'Date of birth cannot be specified in the future',
                                matDatepickerParse: () => 'Incorrect date entered'
                              }
                            },
                            hideExpression: isBusiness
                          },
                        ]
                      },
                      {
                        className: 'formly-sub-column',
                        fieldGroup: [
                          {
                            key: 'percentOwnership',
                            type: 'unseparated-number',
                            templateOptions: {
                              label: '% Ownership',
                              ngxMask: {
                                suffix: '%'
                              },
                              attributes: {
                                'data-cy': 'ownership-input'
                              },
                              min: 0,
                              max: 100,
                              required: true
                            },
                            validation: {
                              show: true,
                            }
                          }
                        ]
                      },
                    ]
                  },
                  {
                    className: 'formly-sub-column',
                    fieldGroup: [
                      {
                        key: 'ssnGuarded',
                        type: 'ssn',
                        templateOptions: {
                          label: 'Social Security #',
                          attributes: {
                            'data-cy': 'ssn-input'
                          },
                          pattern: /^[0-9]{9}/,
                          ngxMask: {
                            hiddenInput: true,
                            patterns: {
                              'B': {
                                pattern: new RegExp('\\d'),
                                symbol: 'X',
                              },
                              '0': {
                                pattern: new RegExp('\\d'),
                              }
                            }
                          }
                        },
                        hideExpression: (model: any) => this.isModelBusiness ? true : model.country === 'en-GB'
                      },
                      {
                        key: 'ein',
                        type: 'unseparated-number',
                        templateOptions: {
                          label: 'EIN',
                          minLength: 9,
                          maxLength: 9,
                          attributes: {
                            'data-cy': 'ein-input'
                          }
                        },
                        hideExpression: !isBusiness
                      },
                    ],
                    hideExpression: (model: any) => this.isModelBusiness ? false : model.country === 'en-GB'
                  }
                ]
              }, // END Date of Birth, Ownership, & Social Security #
              { // BEGIN Street Address 1 & 2, City, State, Zip Code, & Zip+4
                fieldGroupClassName: 'formly-field-group',
                hideExpression: !this.isAnOwner,
                fieldGroup: [
                  {
                    key: 'country',
                    type: 'countries',
                    templateOptions: {
                      label: 'Country',
                      attributes: {
                        'data-cy': 'country-select'
                      }
                    },
                    hideExpression: !this.canSeeCountrySelect
                  },
                  {
                    key: 'street',
                    type: 'text',
                    templateOptions: {
                      label: 'Street Address 1',
                      attributes: {
                        'data-cy': 'street-1-input'
                      }
                    }
                  },
                  {
                    fieldGroupClassName: 'formly-sub-row',
                    fieldGroup: [
                      {
                        className: 'formly-sub-column',
                        fieldGroup: [
                          {
                            key: 'street2',
                            type: 'text',
                            templateOptions: {
                              label: 'Street Address 2',
                              attributes: {
                                'data-cy': 'street-2-input'
                              }
                            }
                          },
                        ]
                      },
                      // Dynamically insert padding
                      {
                        className: 'formly-sub-column-padding',
                        hideExpression: 'model.country !== "en-GB"',
                      },
                      {
                        className: 'formly-sub-column',
                        hideExpression: 'model.country !== "en-GB"',
                        fieldGroup: [
                          {
                            key: 'street3',
                            type: 'text',
                            templateOptions: {
                              label: 'Street Address 3',
                              attributes: {
                                'data-cy': 'street-3-input'
                              }
                            },
                          },
                        ]
                      }
                    ]
                  },
                  {
                    fieldGroupClassName: 'formly-sub-row',
                    fieldGroup: [
                      {
                        className: 'formly-sub-column-2 formly-sub-column-padding',
                        fieldGroup: [
                          {
                            key: 'city',
                            type: 'text',
                            templateOptions: {
                              label: 'City',
                              attributes: {
                                'data-cy': 'city-input'
                              }
                            }
                          }
                        ]
                      },
                      {
                        className: 'formly-sub-column formly-sub-column-padding',
                        hideExpression: 'model.country === "en-GB"',
                        fieldGroup: [
                          {
                            key: 'stateId',
                            type: 'states',
                            templateOptions: {
                              label: 'State',
                              attributes: {
                                'data-cy': 'state-input'
                              }
                            },
                          }
                        ]
                      },
                      {
                        className: 'formly-sub-column',
                        fieldGroup: [
                          {
                            key: 'zipId',
                            type: 'text',
                            templateOptions: {
                              attributes: {
                                'data-cy': 'zip-input'
                              },
                            },
                            expressionProperties: {
                              'templateOptions.label': (model: any) => this.localeSettingService.getLocalePostalCodeName(model.country)
                            },
                            validation: {
                              show: true,
                            },
                            validators: {
                              zipId: {
                                expression: (c) => {
                                  if (c.value) {
                                    return c && c._parent && c._parent.value && c._parent.value.country
                                      ? this.localeSettingService.getLocalePostalCodeRegex(c._parent.value.country).test(c.value)
                                      : this.localeSettingService.getLocalePostalCodeRegex().test(c.value);
                                  }
                                  return true;
                                },
                                message: () => 'Invalid format',
                              },
                            },
                          }
                        ]
                      },
                      // Dynamically insert padding
                      {
                        className: 'formly-sub-column-padding',
                        hideExpression: 'model.country === "en-GB"',
                      },
                      {
                        className: 'formly-sub-column',
                        hideExpression: 'model.country === "en-GB"',
                        fieldGroup: [
                          {
                            key: 'zipExtra',
                            type: 'unseparated-number',
                            templateOptions: {
                              label: 'Zip+4',
                              attributes: {
                                'data-cy': 'zip-extra-input'
                              },
                              pattern: /^[0-9]{4}/,
                              minLength: 4,
                              maxLength: 4,
                            },
                          }
                        ]
                      }
                    ]
                  }
                ]
              } // END Street Address 1 & 2, City, State, Zip Code, & Zip+4
            ]
          }
        ]
      }
    ];
  }
}
